import { useEffect, useState, useCallback } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check } from "lucide-react";
import { toast } from "sonner";

const RELATIVE = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const FULL = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const ctl = useListControls(items, { name: (n) => n.title, fields: (n) => [n.title, n.body], initialSort: "default" });
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=100");
      setItems([...(data.items || [])].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))));
      setUnread(data.unread_count || 0);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNotif = async (n) => {
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await api.post("/notifications/mark-all-read"); toast.success("Marked all as read"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="notifications-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono text-[#CCFF00] inline-flex items-center gap-2"><Bell className="w-3.5 h-3.5" /> NOTIFICATIONS</div>
          <h1 className="h-display text-4xl md:text-5xl tracking-tighter mt-1">Everything worth a nudge.</h1>
          <div className="text-xs text-[#A3A3A3] mt-1">
            {items.length} total · {unread} unread
          </div>
        </div>
        {unread > 0 && (
          <button
            onClick={markAll}
            className="border border-[#404040] hover:border-[#CCFF00] hover:text-[#CCFF00] py-2 px-4 text-xs uppercase tracking-wider label-mono inline-flex items-center gap-2 transition-colors"
            data-testid="notifications-mark-all"
          >
            <Check className="w-3.5 h-3.5" /> Mark all read
          </button>
        )}
      </div>

      {loading && <div className="text-sm text-[#A3A3A3]">Loading…</div>}
      {!loading && !items.length && (
        <div className="card-surface p-8 text-center" data-testid="notifications-empty">
          <Bell className="w-10 h-10 mx-auto text-[#A3A3A3]" strokeWidth={1.5} />
          <div className="h-display text-2xl mt-3">No notifications yet</div>
          <div className="text-sm text-[#A3A3A3] max-w-md mx-auto mt-2">
            You&apos;re all caught up. When a coach quotes, approves a claim, or publishes a window, you&apos;ll hear about it here.
          </div>
          <Link to="/dashboard" className="btn-volt inline-flex mt-4 py-2 px-4 text-xs items-center gap-2">Back to dashboard</Link>
        </div>
      )}

      <div className="space-y-2 fade-up d1">
        {items.length > 0 && <ListControlsBar ctl={ctl} modes={["default", "az", "za"]} placeholder="Search notifications…" testid="notifications" className="mb-3" />}
        {ctl.list.map((n) => (
          <button
            key={n.id}
            onClick={() => openNotif(n)}
            data-testid={`notification-page-item-${n.id}`}
            className={`w-full text-left card-surface p-4 flex items-start gap-3 hover:border-[#CCFF00] transition-colors ${n.is_read ? "opacity-70" : "border-l-4 border-l-[#CCFF00]"}`}
          >
            {!n.is_read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#CCFF00] shrink-0" data-testid={`notification-unread-dot-${n.id}`} />}
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2">
                <div className="text-sm font-semibold flex-1">{n.title}</div>
                {!n.is_read && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#CCFF00] text-black shrink-0">New</span>}
              </div>
              {n.body && <div className="text-xs text-[#A3A3A3] mt-1">{n.body}</div>}
              <div className="text-[10px] text-[#737373] mt-1 label-mono" data-testid={`notification-time-${n.id}`}>{RELATIVE(n.created_at)} · {FULL(n.created_at)}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
